import { useEffect, useState, useMemo, createContext, useContext } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, FileSignature, ShieldCheck, Building2, AlertTriangle, RotateCcw, CheckCircle2, Ban } from "lucide-react";
import { formatPhone, unformatPhone } from "@/lib/phone";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { toast } from "sonner";
import SignaturePad from "@/components/SignaturePad";

const AGE_GROUPS = [
  { id: "infant",     label: "Infants" },
  { id: "toddler",    label: "Toddler" },
  { id: "preschool",  label: "Preschool" },
  { id: "school_age", label: "School-age" },
];

const emptyForm = () => ({
  // Child basics
  first_name: "", last_name: "", date_of_birth: "", age_group: "toddler", room: "", schedule: "Full-time",
  parent_name: "", parent_email: "", parent_phone: "",

  // PAGE 1 — Child + Parents + Emergency + Physician
  address: "", city: "", state: "OH", zip: "", home_phone: "", first_day_at_program: "",

  parent1_name: "", parent1_relationship: "Mother", parent1_cell: "", parent1_email: "",
  parent1_work_name: "", parent1_work_address: "", parent1_work_city: "", parent1_work_state: "OH", parent1_work_phone: "", parent1_reachable_at: "",
  p1_release_name: "No", p1_release_work: false, p1_release_cell: false, p1_release_home: false, p1_release_email: false,
  // iter-80 — new DCY 01234 (Rev 2025+) fields ---------------------------
  // "Same as Child's" toggles for Parent 1 & 2 home addresses. Default true
  // — most families share a home with the child, so the printed form ticks
  // the ☐ automatically. If a parent lives elsewhere, un-tick and fill in
  // their city/state/zip below.
  parent1_same_as_child: true,
  parent1_city: "", parent1_state: "OH", parent1_zip: "",
  parent1_phone: "",  // separate from cell — landline / primary
  //
  // Parent #2 — opt-in. Single-parent households mark this "not applicable"
  // and the printed DCY 01234 stamps a strike-through over the P2 row.
  parent2_not_applicable: false,
  parent2_name: "", parent2_relationship: "", parent2_cell: "", parent2_email: "",
  parent2_work_name: "", parent2_work_address: "", parent2_work_city: "", parent2_work_state: "OH", parent2_work_phone: "", parent2_reachable_at: "",
  p2_release_name: "No", p2_release_work: false, p2_release_cell: false, p2_release_home: false, p2_release_email: false,
  parent2_same_as_child: true,
  parent2_phone: "", parent2_city: "", parent2_state: "OH", parent2_zip: "",

  emergency_contact_name: "", emergency_contact_phone: "", emergency_contact_relation: "",
  emergency_contact_address: "", emergency_contact_city: "", emergency_contact_state: "OH", emergency_contact_zip: "",
  // "Other number or email" free-text field on the new form (per-contact).
  emergency_contact_other: "",
  emergency_contact_may_release: true,
  emergency2_contact_name: "", emergency2_contact_phone: "", emergency2_contact_relation: "",
  emergency2_contact_other: "",
  emergency2_contact_may_release: true,

  // PAGE 2 — Allergies / Conditions / Meds / Diet — ALL default "No"
  allergies_present: "No", allergy_food: false, allergy_medication: false, allergy_environmental: false,
  allergies_detail: "", allergy_monitoring: "No",
  health_condition_present: "No", health_condition_detail: "", health_condition_care_plan: "No",
  medication_use: "No", medication_detail: "", medication_at_program: "No",
  dietary_restrictions: "No", dietary_detail: "", dietary_modified_diet: "N/A",

  // Legacy generic textarea fields (kept so existing endpoint still accepts them)
  allergies: "N/A", medications: "N/A", medical_conditions: "N/A",

  // PAGE 3 — Not applicable default
  hospitalization_history: "", hospitalization_na: true,
  comfort_fears: "", comfort_fears_na: true,
  habits: "", habits_na: true,
  routines_behavior: "", routines_behavior_na: true,

  // PAGE 4 — Diapering, Transport, Policies, Signature
  toilet_trained: "No",
  program_diaper_interval: "2", diaper_schedule_choice: "", diaper_custom_hours: "",
  transportation_authorized: true, no_transport_action: "",
  policies_acknowledged: "Yes",
  parent_roster_consent: true, photo_consent: true, sunscreen_consent: true,
  authorized_pickup: "", not_released_to: "",

  // iter-80 — new DCY 01234 (Rev 2025+) narrative + acknowledgement blocks
  //   `development_info`   → optional "Information on my child's development"
  //   `accommodation_notes`→ optional "The following accommodation may be helpful…"
  //   `specialized_services_present` → checkbox: child receives specialized/individual services at the program
  //   `health_care_plan_on_file`     → checkbox: a Health Care Plan / physician
  //                                    doc / electronic equivalent has been provided
  // When narrative blocks are blank the printed form auto-ticks the matching NA
  // checkbox (backend defaults do this on the fly).
  development_info: "",
  accommodation_notes: "",
  specialized_services_present: false,
  health_care_plan_on_file: false,

  signature: "", signed_by_email: "", signature_date: new Date().toISOString().slice(0, 10),
});

/**
 * iter-81bf — Missing-field highlight feature.
 *
 * State-required DCY 01234 fields grouped by the wizard tab they live on, so
 * a missing field can steer the user to the right step. The set is
 * intentionally conservative — it mirrors what Ohio's licensing inspector
 * looks for on a printed enrollment: child identity, home address, at least
 * one parent contact, one emergency contact, and the parent signature.
 *
 * Fields that are only required conditionally (e.g. Parent #2 fields when
 * P2 is NOT marked "not applicable") are added by `computeMissing()` below.
 */
const REQUIRED_FIELDS_BY_TAB = {
  page1: [
    "last_name", "first_name", "date_of_birth",
    "address", "city", "zip",
    "parent1_name", "parent1_cell", "parent1_email",
    "emergency_contact_name", "emergency_contact_phone",
  ],
  page4: ["signature", "signed_by_email"],
};

/** Return {missing: Set<string>, firstTab: string|null}. */
function computeMissing(f) {
  const missing = new Set();
  const isBlank = (v) => v === null || v === undefined || String(v).trim() === "";
  for (const tab of Object.keys(REQUIRED_FIELDS_BY_TAB)) {
    for (const key of REQUIRED_FIELDS_BY_TAB[tab]) {
      if (isBlank(f[key])) missing.add(key);
    }
  }
  // Parent #2 conditional requirement — only when P2 is NOT marked N/A.
  if (!f.parent2_not_applicable) {
    for (const key of ["parent2_name", "parent2_cell"]) {
      if (isBlank(f[key])) missing.add(key);
    }
  }
  // Emergency-contact release Yes/No (radio — never blank in the initial
  // form state but guarded here in case someone clears it programmatically).
  if (typeof f.emergency_contact_may_release !== "boolean") {
    missing.add("emergency_contact_may_release");
  }

  // Resolve which tab holds the first missing field so we can auto-jump.
  const FIELD_TO_TAB = {};
  for (const [tab, keys] of Object.entries(REQUIRED_FIELDS_BY_TAB)) {
    for (const k of keys) FIELD_TO_TAB[k] = tab;
  }
  FIELD_TO_TAB.parent2_name = "page1";
  FIELD_TO_TAB.parent2_cell = "page1";
  FIELD_TO_TAB.emergency_contact_may_release = "page1";

  const tabOrder = ["page1", "page4"];
  let firstTab = null;
  outer: for (const tab of tabOrder) {
    for (const k of missing) {
      if (FIELD_TO_TAB[k] === tab) { firstTab = tab; break outer; }
    }
  }
  return { missing, firstTab };
}

/** Highlight-missing context — each Field / YesNo / Check reads this set to
 *  decide whether to render itself with a soft-yellow "please fill me in"
 *  background. The set is populated only after the user tries to submit
 *  with blanks; it clears the moment they type into the field. */
const MissingContext = createContext({ missing: new Set(), clear: () => {} });

export default function AddChildEnrollmentForm({ onCreated, editingChild = null, initialEnrollment = null, initialChild = null }) {
  const isEdit = !!editingChild;
  const [f, setF] = useState(() => {
    const base = emptyForm();
    // iter-51 — `initialChild` is used by the "Add sibling" flow on the
    // child profile.  Same shape as `editingChild` (legacy parent fields)
    // but doesn't flip `isEdit` — submitting still creates a brand-new
    // record.  We apply it BEFORE `editingChild` so an explicit edit wins.
    const seedChild = editingChild || initialChild;
    if (seedChild) {
      Object.assign(base, {
        first_name: seedChild.first_name || "",
        last_name: seedChild.last_name || "",
        date_of_birth: seedChild.date_of_birth || "",
        age_group: seedChild.age_group || "toddler",
        room: seedChild.room || "",
        schedule: seedChild.schedule || "Full-time",
        parent_name: seedChild.parent_name || "",
        parent_email: seedChild.parent_email || "",
        parent_phone: seedChild.parent_phone || "",
      });
    }
    if (initialEnrollment) {
      for (const [k, v] of Object.entries(initialEnrollment)) {
        if (v !== null && v !== undefined && k in base) base[k] = v;
      }
    }
    return base;
  });

  // ---- Draft auto-save ----------------------------------------------------
  // For NEW enrollments (not edits, not siblings), we persist the form
  // state to localStorage every ~2s so an interrupted parent/director can
  // pick up exactly where they left off. The draft is cleared on success.
  // Sibling adds skip the draft so the parent prefill isn't overwritten.
  const DRAFT_KEY = (isEdit || initialChild) ? null : "childcheck:addChildDraft:v1";
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [draftResumed, setDraftResumed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [section, setSection] = useState("page1");
  const [center, setCenter] = useState(null);
  // iter-81bf — soft-yellow highlight on any state-required blank field
  // right before printing. Populated only after the user hits Submit with
  // blanks; cleared field-by-field as they type.
  const [missing, setMissing] = useState(() => new Set());
  const clearMissing = (key) => setMissing((prev) => {
    if (!prev.has(key)) return prev;
    const next = new Set(prev);
    next.delete(key);
    return next;
  });
  const missingCtx = useMemo(() => ({ missing, clear: clearMissing }), [missing]);

  useEffect(() => {
    if (!DRAFT_KEY) { setDraftLoaded(true); return; }
    try {
      const raw = window.localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.f && parsed.section) {
          setF((prev) => ({ ...prev, ...parsed.f }));
          setSection(parsed.section);
          setDraftResumed(true);
          toast.message("Resumed your saved draft", {
            description: parsed.savedAt ? `Last edit ${new Date(parsed.savedAt).toLocaleString()}` : undefined,
          });
        }
      }
    } catch { /* ignore corrupt draft */ }
    setDraftLoaded(true);
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced write — 1.5s after the last keystroke.
  useEffect(() => {
    if (!DRAFT_KEY || !draftLoaded) return;
    const id = setTimeout(() => {
      try {
        window.localStorage.setItem(DRAFT_KEY, JSON.stringify({
          f, section, savedAt: new Date().toISOString(),
        }));
      } catch { /* quota exceeded — ignore */ }
    }, 1500);
    return () => clearTimeout(id);
  }, [f, section, DRAFT_KEY, draftLoaded]);

  const clearDraft = () => { try { DRAFT_KEY && window.localStorage.removeItem(DRAFT_KEY); } catch { /* noop */ } };

  // Sync the legacy parent_name / parent_email / parent_phone fields with
  // Parent #1 — Parent #1 is the source of truth (it's the only thing the UI
  // binds to). The sync used to only copy when the legacy field was empty,
  // which captured just the FIRST keystroke and then froze; the saved name
  // ended up as a single letter ("F" / "Y") instead of the full name. Now we
  // always mirror Parent #1 → legacy, and only fall back to legacy → Parent
  // #1 when editing existing children with no Parent #1 record yet.
  useEffect(() => {
    setF((x) => {
      const next = { ...x };
      // Authoritative: Parent #1 → legacy mirror
      if (next.parent1_name) next.parent_name = next.parent1_name;
      if (next.parent1_email) next.parent_email = next.parent1_email;
      if (next.parent1_cell) next.parent_phone = next.parent1_cell;
      // Bootstrap: when editing a legacy child that has no Parent #1 yet,
      // copy the legacy value INTO Parent #1 so the wizard renders it.
      if (!next.parent1_name && next.parent_name) next.parent1_name = next.parent_name;
      if (!next.parent1_email && next.parent_email) next.parent1_email = next.parent_email;
      if (!next.parent1_cell && next.parent_phone) next.parent1_cell = next.parent_phone;
      return next;
    });
  }, [f.parent1_name, f.parent1_email, f.parent1_cell]);  // eslint-disable-line react-hooks/exhaustive-deps

  const up = (k, v) => {
    setF((x) => ({ ...x, [k]: v }));
    // iter-81bf — Clear the highlight the moment the user edits a
    // required-but-missing field. Keeps the "please fill me in" cue from
    // lingering after the field is filled.
    if (missing.has(k)) clearMissing(k);
  };

  useEffect(() => { api.get("/center/me").then((r) => setCenter(r.data)).catch(() => {}); }, []);

  // Wizard step list — iter-81c1: reduced from 4 tabs to 2 tabs so the
  // wizard mirrors the two physical pages of DCY 01234 v2025 exactly.
  // Page 1 = Identification (child + parents + emergency contacts).
  // Page 2 = Health / Services / Signature (mirrors printed Page 2).
  // Legacy medical/allergy/dietary/care-notes/pickup fields still exist
  // in form state for the audit binder — they just don't get their own
  // wizard step anymore since they aren't on the state's printed form.
  const tabs = [
    { id: "page1", label: "Identification", n: 1 },
    { id: "page4", label: "Services & Sign", n: 2 },
  ];

  const submit = async (e) => {
    e.preventDefault();
    // Defense-in-depth: even though Submit is only rendered on page 4 now,
    // refuse to commit if section drifted away (e.g. a programmatic call).
    if (section !== "page4") return;

    // iter-81bf — Missing-field highlight. Before printing/filing, check
    // every state-required field. If any are blank, light them up in soft
    // yellow so the director can see what to fill in — and jump them to
    // the tab that has the first missing field.
    const { missing: missingSet, firstTab } = computeMissing(f);
    if (missingSet.size > 0) {
      setMissing(missingSet);
      const count = missingSet.size;
      toast.error(
        `${count} required field${count === 1 ? "" : "s"} missing`,
        { description: "These are highlighted in yellow. Fill them in before printing so nothing goes to the state with gaps." }
      );
      if (firstTab && firstTab !== section) setSection(firstTab);
      return;
    }
    // Clear any lingering highlight if the user re-submits after fixing.
    if (missing.size > 0) setMissing(new Set());

    // Auto-sync the legacy parent_name / parent_email fields from the richer
    // Parent/Guardian #1 inputs so directors don't have to type them twice.
    // (The UI only collects parent1_name + parent1_email — the older
    // parent_name/parent_email keys were required but never bound to an input,
    // which caused the false "Child name, DOB, parent name and email are
    // required" error when everything looked filled.)
    const parent_name = (f.parent_name || f.parent1_name || "").trim();
    const parent_email = (f.parent_email || f.parent1_email || "").trim();
    if (!f.first_name || !f.last_name || !f.date_of_birth || !parent_name || !parent_email) {
      return toast.error("Child name, DOB, parent name and email are required.");
    }
    // Persist the synced values onto the form so the submit payload + legacy
    // consumers (e.g. SignatureBlock placeholders) see the same value.
    f.parent_name = parent_name;
    f.parent_email = parent_email;
    // Hard block: emergency contact can't be a parent.  We no longer JUMP
    // back to page 1 (that's what gave the "kicked back to the start"
    // feeling) — we just toast and let the director correct it on whatever
    // page they're on.
    const norm = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, " ");
    const parentNames = [f.parent_name, f.parent1_name, f.parent2_name].map(norm).filter(Boolean);
    if (f.emergency_contact_name && parentNames.includes(norm(f.emergency_contact_name))) {
      return toast.error("Emergency contact cannot be a parent/guardian. Use a grandparent, relative, or trusted friend.");
    }
    if (f.emergency2_contact_name && parentNames.includes(norm(f.emergency2_contact_name))) {
      return toast.error("Secondary emergency contact cannot be a parent/guardian.");
    }

    // iter-50 — `enrollmentReady` only gates whether the DCY 01234 is treated
    // as fully SIGNED.  All the other wizard data (address, parent work info,
    // emergency contact, medical, etc.) must ALWAYS be persisted.  The
    // earlier condition used `enrollmentReady` to gate `body.enrollment`,
    // which silently threw away every wizard field for any child saved
    // without a signature — that's the "parent info disappearing" bug.
    const enrollmentReady = f.address && f.city && f.zip && f.emergency_contact_name && f.emergency_contact_phone && f.signature && f.signed_by_email;
    const hasAnyEnrollmentData = !!(
      f.address || f.city || f.zip || f.home_phone ||
      f.parent1_work_name || f.parent1_work_address ||
      f.emergency_contact_name || f.signature
    );

    const body = {
      first_name: f.first_name, last_name: f.last_name, date_of_birth: f.date_of_birth,
      age_group: f.age_group, room: f.room || null, schedule: f.schedule || "Full-time",
      parent_name: f.parent_name, parent_email: f.parent_email, parent_phone: f.parent_phone || null,
    };
    if (hasAnyEnrollmentData) {
      // Send every field the new DCY 01234 Rev 8/2025 expects
      const copy = { ...f };
      delete copy.first_name; delete copy.last_name; delete copy.date_of_birth;
      delete copy.age_group; delete copy.room; delete copy.schedule;
      delete copy.parent_name; delete copy.parent_email; delete copy.parent_phone;
      // Single-parent household — wipe every Parent #2 field so nothing prints
      // in the P2 row except the strike-through stamp drawn at PDF time.
      if (f.parent2_not_applicable) {
        copy.parent2_name = ""; copy.parent2_relationship = "";
        copy.parent2_cell = ""; copy.parent2_email = "";
        copy.parent2_work_name = ""; copy.parent2_work_address = "";
        copy.parent2_work_city = ""; copy.parent2_work_state = "";
        copy.parent2_work_phone = ""; copy.parent2_reachable_at = "";
        copy.p2_release_name = "No";
        copy.p2_release_work = false; copy.p2_release_cell = false;
        copy.p2_release_home = false; copy.p2_release_email = false;
      }
      // Keep legacy textarea mirrors synced with Yes/No state
      copy.allergies = f.allergies_present === "Yes" ? (f.allergies_detail || "Yes") : "N/A";
      copy.medical_conditions = f.health_condition_present === "Yes" ? (f.health_condition_detail || "Yes") : "N/A";
      copy.medications = f.medication_use === "Yes" ? (f.medication_detail || "Yes") : "N/A";
      body.enrollment = copy;
    }

    setSaving(true);
    try {
      let childId = null;
      if (isEdit) {
        await api.put(`/children/${editingChild.id}`, body);
        childId = editingChild.id;
        toast.success(enrollmentReady ? `${f.first_name} updated — DCY 01234 re-filed.` : `${f.first_name} updated.`);
      } else {
        const res = await api.post("/children", body);
        childId = res?.data?.id || null;
        toast.success(enrollmentReady ? `${f.first_name} added and DCY 01234 filed.` : `${f.first_name} added — DCY 01234 can be signed from their profile.`);
      }

      // If the enrollment was filed, open the filled DCY 01234 PDF in a new
      // tab right away so the director can print it without re-entering data.
      // This is the "print once done" behaviour the user asked for.
      if (enrollmentReady && childId) {
        try {
          const pdf = await api.get(`/children/${childId}/forms/jfs_01234/pdf`, { responseType: "blob" });
          const url = window.URL.createObjectURL(new Blob([pdf.data], { type: "application/pdf" }));
          window.open(url, "_blank", "noopener,noreferrer");
          setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
        } catch (err) {
          // Non-fatal — the child is saved; director can re-open the PDF from the profile.
          console.warn("auto-open filed DCY 01234 failed", err);
        }
      }

      // iter-61 — guard against the parent component not providing
      // onCreated. Previously a typo in the prop name ("onSaved" vs
      // "onCreated") crashed the form with "n is not a function" after
      // a successful save.
      if (typeof onCreated === "function") onCreated();
      clearDraft();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const currentTabIdx = tabs.findIndex((t) => t.id === section);

  return (
    // iter-48 final fix: NEVER let the form auto-submit. Use a plain <div>
    // wrapper so accidental Enter keypresses + browser quirks can't fire
    // submit halfway through the wizard. The only thing that saves now is
    // an explicit click on the Submit button at the bottom of Page 4 — its
    // onClick directly calls `submit()`.
    <div data-testid="add-child-form" className="space-y-5 pt-1">
      {center && (
        <div data-testid="enroll-center-banner" className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50/60 px-3 py-2">
          <span className="w-7 h-7 rounded-lg bg-emerald-700 grid place-items-center text-white"><Building2 className="w-3.5 h-3.5" /></span>
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-emerald-700">Program / Home</div>
            <div className="text-sm font-medium text-stone-900 truncate">{center.name}{center.license_number ? <span className="text-stone-500"> · Lic. {center.license_number}</span> : null}</div>
          </div>
          <div className="ml-auto text-xs uppercase tracking-[0.16em] font-semibold text-stone-500">DCY 01234 · Rev 8/2025</div>
        </div>
      )}

      {draftResumed && !isEdit && (
        <div data-testid="draft-resumed-banner" className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5">
          <span className="w-7 h-7 rounded-lg bg-amber-500 grid place-items-center text-white">
            <RotateCcw className="w-3.5 h-3.5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-amber-900">Resumed where you left off</div>
            <div className="text-xs text-amber-800">Your draft was auto-saved to this browser. Finish when you're ready.</div>
          </div>
          <Button
            type="button"
            variant="outline"
            data-testid="draft-discard-btn"
            onClick={() => {
              if (window.confirm("Discard the auto-saved draft and start blank?")) {
                clearDraft();
                setDraftResumed(false);
                window.location.reload();
              }
            }}
            className="rounded-xl h-8 text-xs border-amber-300 text-amber-900 hover:bg-amber-100"
          >
            Discard
          </Button>
        </div>
      )}

      <div className="rounded-xl bg-stone-100 p-1.5 sticky top-0 z-10 shadow-sm" data-testid="wizard-stepper">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-1">
          {tabs.map((t, i) => {
            const active = section === t.id;
            const visited = i <= currentTabIdx;
            return (
              <button
                key={t.id}
                type="button"
                data-testid={`add-tab-${t.id}`}
                onClick={() => setSection(t.id)}
                title={`Go to step ${t.n} · ${t.label}`}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-lg transition-all text-sm font-semibold cursor-pointer ${
                  active
                    ? "bg-emerald-700 text-white shadow"
                    : visited
                    ? "bg-white text-stone-900 hover:bg-emerald-50 hover:text-emerald-800"
                    : "text-stone-500 hover:bg-white hover:text-stone-900"
                }`}
              >
                <span className={`w-6 h-6 rounded-full grid place-items-center text-xs font-bold flex-shrink-0 ${
                  active ? "bg-white text-emerald-700" : visited ? "bg-emerald-100 text-emerald-800" : "bg-stone-200 text-stone-500"
                }`}>
                  {t.n}
                </span>
                <span className="truncate">{t.label}</span>
              </button>
            );
          })}
        </div>
        <div className="px-3 pt-2 pb-1 text-[11px] text-stone-500">
          Click any step to jump back — your work auto-saves as you go.
        </div>
      </div>

      {section === "page1" && (
        <MissingContext.Provider value={missingCtx}>
          <Page1 f={f} up={up} />
        </MissingContext.Provider>
      )}
      {section === "page4" && (
        <MissingContext.Provider value={missingCtx}>
          <Page4 f={f} up={up} isEdit={isEdit} />
        </MissingContext.Provider>
      )}

      {section === "page4" && missing.size > 0 && (
        <div data-testid="missing-fields-banner" className="rounded-xl border border-amber-400 bg-amber-50 p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <div className="font-semibold">
              {missing.size} required {missing.size === 1 ? "field is" : "fields are"} still blank.
            </div>
            <div className="mt-0.5 text-xs text-amber-800">
              They're highlighted in yellow on the pages above — fill them in so the printed DCY 01234 is complete before it goes to the state.
            </div>
          </div>
        </div>
      )}

      {section === "page4" && <CompletenessChecklist f={f} setSection={setSection} />}

      <div className="flex items-center justify-between pt-3 border-t border-stone-200">
        <div className="text-sm text-stone-600 font-medium">Step {currentTabIdx + 1} of {tabs.length}</div>
        <div className="flex items-center gap-2">
          {section !== "page1" && (
            <Button type="button" variant="outline" data-testid="add-prev" className="rounded-xl h-10"
              onClick={() => setSection(tabs[Math.max(0, currentTabIdx - 1)].id)}>
              ← Back to {tabs[currentTabIdx - 1].label}
            </Button>
          )}
          {/* iter-50 — render BOTH buttons unconditionally and toggle their
              visibility with `hidden`.  In the previous ternary, the
              Continue and Submit buttons shared the same DOM position, so
              React reconciled them onto the same <button> node — a third
              click on Continue could (under certain timing windows) be
              dispatched to the freshly-mounted Submit handler and commit
              the form. Two separate static nodes + e.stopPropagation()
              eliminate that class of bug entirely. */}
          <Button
            type="button"
            data-testid="add-next"
            hidden={section === "page4"}
            aria-hidden={section === "page4"}
            className={`bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-10 ${section === "page4" ? "hidden" : ""}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const next = tabs[currentTabIdx + 1];
              if (next) setSection(next.id);
            }}
          >
            Continue to {tabs[currentTabIdx + 1]?.label || ""} →
          </Button>
          <Button
            type="button"
            data-testid="add-child-submit"
            hidden={section !== "page4"}
            aria-hidden={section !== "page4"}
            disabled={saving}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              submit({ preventDefault() {} });
            }}
            className={`bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-10 ${section !== "page4" ? "hidden" : ""}`}
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {isEdit ? "Saving…" : "Filing…"}</> : (isEdit ? "Save changes" : "Add child + file DCY 01234")}
          </Button>
        </div>
      </div>
    </div>
  );
}


/**
 * Surfaces blank "important-but-not-strictly-required" fields right above the
 * submit button so directors can see — and one-click jump back to fix — the
 * fields that always-end-up-blank (parent work info, emergency city, physician
 * address). Driven by user feedback after a marketing PDF shipped with several
 * empty rows the director assumed had been filled.
 */
function CompletenessChecklist({ f, setSection }) {
  const issues = [];
  // Parent #1 work info — name, address, city, state, phone
  if (!f.parent1_work_name?.trim() || !f.parent1_work_address?.trim() || !f.parent1_work_city?.trim() || !f.parent1_work_phone?.trim()) {
    issues.push({ to: "page1", label: "Parent #1 work info (name, address, city, phone)" });
  }
  // Parent #2 work info — only required when P2 is filled and not marked N/A
  if (!f.parent2_not_applicable && f.parent2_name?.trim() && (!f.parent2_work_name?.trim() || !f.parent2_work_address?.trim() || !f.parent2_work_city?.trim() || !f.parent2_work_phone?.trim())) {
    issues.push({ to: "page1", label: "Parent #2 work info (name, address, city, phone)" });
  }
  // Emergency contact #1 city + state
  if (f.emergency_contact_name?.trim() && (!f.emergency_contact_city?.trim() || !f.emergency_contact_state?.trim())) {
    issues.push({ to: "page2", label: "Emergency contact #1 — city and state" });
  }
  // Emergency contact #2 city + state (only if a second contact was named)
  if (f.emergency2_contact_name?.trim() && (!f.emergency2_contact_city?.trim() || !f.emergency2_contact_state?.trim())) {
    issues.push({ to: "page2", label: "Emergency contact #2 — city and state" });
  }
  if (issues.length === 0) {
    return (
      <div data-testid="completeness-ok" className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4 text-sm text-emerald-900">
        ✓ Every important field is filled. Ohio DCY 01234 will print clean.
      </div>
    );
  }
  return (
    <div data-testid="completeness-warn" className="rounded-xl border border-amber-300 bg-amber-50 p-4 space-y-2.5">
      <div className="text-sm font-semibold text-amber-900">
        Heads up — {issues.length} {issues.length === 1 ? "section is" : "sections are"} blank.
      </div>
      <p className="text-xs text-amber-800">
        These will print as empty rows on the official enrollment PDF. Click any item below to jump back and fill it in.
      </p>
      <ul className="space-y-1.5">
        {issues.map((i, idx) => (
          <li key={idx}>
            <button
              type="button"
              data-testid={`completeness-fix-${idx}`}
              onClick={() => setSection(i.to)}
              className="text-left w-full text-xs text-amber-900 hover:text-amber-950 hover:underline flex items-center gap-1.5"
            >
              <span className="text-amber-700">→</span> {i.label}
            </button>
          </li>
        ))}
      </ul>
      <div className="text-[11px] text-amber-700/80 pt-1 border-t border-amber-200">
        You can still submit with blank fields — this is just a friendly nudge so nothing is missed.
      </div>
    </div>
  );
}

/* ---------- Page 1 ---------- */
function Page1({ f, up }) {
  return (
    <div className="space-y-5">
      <Section title="Child's information">
        <Row>
          <Field label="Last name" testId="add-last-name" field="last_name" value={f.last_name} onChange={(v) => up("last_name", v)} required />
          <Field label="First name" testId="add-first-name" field="first_name" value={f.first_name} onChange={(v) => up("first_name", v)} required />
        </Row>
        <Row>
          <Field label="Date of birth" type="date" testId="add-dob" field="date_of_birth" value={f.date_of_birth} onChange={(v) => up("date_of_birth", v)} required />
          <Field label="First day at program / home" type="date" testId="add-fday" value={f.first_day_at_program} onChange={(v) => up("first_day_at_program", v)} />
        </Row>
        <Row>
          <div>
            <Label className="text-sm font-medium text-stone-700">Age group</Label>
            <Select value={f.age_group} onValueChange={(v) => up("age_group", v)}>
              <SelectTrigger data-testid="add-age-group" className="mt-1 h-11"><SelectValue /></SelectTrigger>
              <SelectContent>{AGE_GROUPS.map((g) => <SelectItem key={g.id} value={g.id}>{g.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm font-medium text-stone-700">Schedule</Label>
            <Select value={f.schedule} onValueChange={(v) => up("schedule", v)}>
              <SelectTrigger data-testid="add-schedule" className="mt-1 h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Full-time">Full-time</SelectItem>
                <SelectItem value="Part-time">Part-time</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Row>
        <Field label="Room (classroom)" testId="add-room" value={f.room} onChange={(v) => up("room", v)} />
        <AddressAutocomplete
          label="Home address"
          testId="add-address"
          value={f.address}
          onStreet={(v) => up("address", v)}
          onCity={(v) => up("city", v)}
          onState={(v) => up("state", v)}
          onZip={(v) => up("zip", v)}
        />
        <Row cols={4}>
          <Field label="City" testId="add-city" field="city" value={f.city} onChange={(v) => up("city", v)} />
          <Field label="State" testId="add-state" value={f.state} onChange={(v) => up("state", v)} />
          <Field label="ZIP" testId="add-zip" field="zip" value={f.zip} onChange={(v) => up("zip", v)} />
          <Field label="Home phone" testId="add-home-phone" value={f.home_phone} onChange={(v) => up("home_phone", v)} format="phone" />
        </Row>
      </Section>

      <Section title="Parent/Guardian #1">
        <div className="rounded-xl border border-emerald-200 bg-white p-3 mb-3">
          <Check
            testId="p1-same-as-child"
            checked={f.parent1_same_as_child}
            onChange={(v) => up("parent1_same_as_child", v)}
            label={<span><span className="font-semibold">Home address:</span> same as child&apos;s</span>}
          />
          {!f.parent1_same_as_child && (
            <div data-testid="p1-address-block" className="mt-3 space-y-2">
              <Row cols={3}>
                <Field label="City" testId="add-p1-city" value={f.parent1_city} onChange={(v) => up("parent1_city", v)} />
                <Field label="State" testId="add-p1-state" value={f.parent1_state} onChange={(v) => up("parent1_state", v)} />
                <Field label="Zip" testId="add-p1-zip" value={f.parent1_zip} onChange={(v) => up("parent1_zip", v)} />
              </Row>
            </div>
          )}
        </div>
        <Row>
          <Field label="Full name" testId="add-p1-name" field="parent1_name" value={f.parent1_name} onChange={(v) => up("parent1_name", v)} placeholder={f.parent_name} />
          <Field label="Relationship to child" testId="add-p1-rel" value={f.parent1_relationship} onChange={(v) => up("parent1_relationship", v)} placeholder="Mother" />
        </Row>
        <Row>
          <Field label="Phone number" testId="add-p1-phone" value={f.parent1_phone} onChange={(v) => up("parent1_phone", v)} format="phone" />
          <Field label="Cell phone (if applicable)" testId="add-p1-cell" field="parent1_cell" value={f.parent1_cell} onChange={(v) => up("parent1_cell", v)} format="phone" />
        </Row>
        <Field label="Email" type="email" testId="add-p1-email" field="parent1_email" value={f.parent1_email} onChange={(v) => up("parent1_email", v)} />
        <Field label="Work/School name" testId="add-p1-work" value={f.parent1_work_name} onChange={(v) => up("parent1_work_name", v)} />
        <AddressAutocomplete
          label="Work/School street address"
          testId="add-p1-work-addr"
          value={f.parent1_work_address}
          onStreet={(v) => up("parent1_work_address", v)}
          onCity={(v) => up("parent1_work_city", v)}
          onState={(v) => up("parent1_work_state", v)}
          onZip={() => {}}
        />
        <Row>
          <Field label="Work/School city" testId="add-p1-work-city" value={f.parent1_work_city} onChange={(v) => up("parent1_work_city", v)} />
          <Field label="Work/School state" testId="add-p1-work-state" value={f.parent1_work_state} onChange={(v) => up("parent1_work_state", v)} />
          <Field label="Work/School phone" testId="add-p1-work-phone" value={f.parent1_work_phone} onChange={(v) => up("parent1_work_phone", v)} format="phone" />
        </Row>
        <Field label="Where can you be reached while child is in care?" testId="add-p1-reach" value={f.parent1_reachable_at} onChange={(v) => up("parent1_reachable_at", v)} />
        <YesNo
          testId="p1-release"
          label="Release this name to other parents if they request a contact roster?"
          value={f.p1_release_name} onChange={(v) => up("p1_release_name", v)}
          help="Ohio rule 5180:2-12-15 — parents must be offered the choice. Default is No."
        />
        {f.p1_release_name === "Yes" && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 space-y-2" data-testid="p1-release-picker">
            <div className="text-sm font-medium text-emerald-900">If yes, which contact details should be shared?</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Check testId="p1-release-work"  checked={f.p1_release_work}  onChange={(v) => up("p1_release_work", v)}  label="Work #" />
              <Check testId="p1-release-cell"  checked={f.p1_release_cell}  onChange={(v) => up("p1_release_cell", v)}  label="Cell #" />
              <Check testId="p1-release-home"  checked={f.p1_release_home}  onChange={(v) => up("p1_release_home", v)}  label="Home #" />
              <Check testId="p1-release-email" checked={f.p1_release_email} onChange={(v) => up("p1_release_email", v)} label="Email" />
            </div>
          </div>
        )}
      </Section>

      <Section title="Parent/Guardian #2 (optional)">
        <div className="rounded-xl border border-stone-200 bg-stone-50 p-3 mb-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-2 text-sm text-stone-700 min-w-0">
            <Ban className="w-4 h-4 mt-0.5 shrink-0 text-stone-500" />
            <div>
              <div className="font-semibold text-stone-900">Single-parent household?</div>
              <div className="text-xs text-stone-600">Mark Parent #2 as not applicable — the printed DCY 01234 will show a strike-through across the row instead of blank fields.</div>
            </div>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer text-sm select-none" data-testid="p2-na-toggle-wrap">
            <input
              type="checkbox"
              data-testid="p2-not-applicable-toggle"
              checked={!!f.parent2_not_applicable}
              onChange={(e) => up("parent2_not_applicable", e.target.checked)}
              className="w-4 h-4 accent-stone-700"
            />
            <span className="font-semibold text-stone-800">Parent #2 not applicable</span>
          </label>
        </div>
        <fieldset
          disabled={!!f.parent2_not_applicable}
          aria-disabled={!!f.parent2_not_applicable}
          data-testid="p2-fields-wrap"
          className={f.parent2_not_applicable ? "opacity-50 line-through pointer-events-none select-none" : ""}
        >
        <div className="rounded-xl border border-emerald-200 bg-white p-3 mb-3">
          <Check
            testId="p2-same-as-child"
            checked={f.parent2_same_as_child}
            onChange={(v) => up("parent2_same_as_child", v)}
            label={<span><span className="font-semibold">Home address:</span> same as child&apos;s</span>}
          />
          {!f.parent2_same_as_child && (
            <div data-testid="p2-address-block" className="mt-3 space-y-2">
              <Row cols={3}>
                <Field label="City" testId="add-p2-city" value={f.parent2_city} onChange={(v) => up("parent2_city", v)} />
                <Field label="State" testId="add-p2-state" value={f.parent2_state} onChange={(v) => up("parent2_state", v)} />
                <Field label="Zip" testId="add-p2-zip" value={f.parent2_zip} onChange={(v) => up("parent2_zip", v)} />
              </Row>
            </div>
          )}
        </div>
        <Row>
          <Field label="Full name" testId="add-p2-name" field="parent2_name" value={f.parent2_name} onChange={(v) => up("parent2_name", v)} />
          <Field label="Relationship to child" testId="add-p2-rel" value={f.parent2_relationship} onChange={(v) => up("parent2_relationship", v)} />
        </Row>
        <Row>
          <Field label="Phone number" testId="add-p2-phone" value={f.parent2_phone} onChange={(v) => up("parent2_phone", v)} format="phone" />
          <Field label="Cell phone (if applicable)" testId="add-p2-cell" field="parent2_cell" value={f.parent2_cell} onChange={(v) => up("parent2_cell", v)} format="phone" />
        </Row>
        <Field label="Email" type="email" testId="add-p2-email" value={f.parent2_email} onChange={(v) => up("parent2_email", v)} />
        <Field label="Work/School name" testId="add-p2-work" value={f.parent2_work_name} onChange={(v) => up("parent2_work_name", v)} />
        <AddressAutocomplete
          label="Work/School street address"
          testId="add-p2-work-addr"
          value={f.parent2_work_address}
          onStreet={(v) => up("parent2_work_address", v)}
          onCity={(v) => up("parent2_work_city", v)}
          onState={(v) => up("parent2_work_state", v)}
          onZip={() => {}}
        />
        <Row>
          <Field label="Work/School city" testId="add-p2-work-city" value={f.parent2_work_city} onChange={(v) => up("parent2_work_city", v)} />
          <Field label="Work/School state" testId="add-p2-work-state" value={f.parent2_work_state} onChange={(v) => up("parent2_work_state", v)} />
          <Field label="Work/School phone" testId="add-p2-work-phone" value={f.parent2_work_phone} onChange={(v) => up("parent2_work_phone", v)} format="phone" />
        </Row>
        <YesNo
          testId="p2-release"
          label="Release this name to other parents if they request a contact roster?"
          value={f.p2_release_name} onChange={(v) => up("p2_release_name", v)}
          help="Ohio rule 5180:2-12-15 — parents must be offered the choice. Default is No."
        />
        {f.p2_release_name === "Yes" && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 space-y-2" data-testid="p2-release-picker">
            <div className="text-sm font-medium text-emerald-900">If yes, which contact details should be shared?</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Check testId="p2-release-work"  checked={f.p2_release_work}  onChange={(v) => up("p2_release_work", v)}  label="Work #" />
              <Check testId="p2-release-cell"  checked={f.p2_release_cell}  onChange={(v) => up("p2_release_cell", v)}  label="Cell #" />
              <Check testId="p2-release-home"  checked={f.p2_release_home}  onChange={(v) => up("p2_release_home", v)}  label="Home #" />
              <Check testId="p2-release-email" checked={f.p2_release_email} onChange={(v) => up("p2_release_email", v)} label="Email" />
            </div>
          </div>
        )}
        </fieldset>
      </Section>

      <Section title="Emergency contacts">
        <div data-testid="enroll-emergency-warning" className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <b>Parents cannot be listed as emergency contacts.</b> Use a grandparent, relative, or trusted friend (18+) who can take responsibility when parents are unreachable.
          </div>
        </div>
        <div className="text-sm uppercase tracking-[0.14em] font-bold text-stone-600 mt-3">Emergency contact #1</div>
        <Row>
          <Field label="Name" testId="add-e1-name" field="emergency_contact_name" value={f.emergency_contact_name} onChange={(v) => up("emergency_contact_name", v)} />
          <Field label="Relationship" testId="add-e1-rel" value={f.emergency_contact_relation} onChange={(v) => up("emergency_contact_relation", v)} placeholder="Grandparent" />
        </Row>
        <Row>
          <Field label="Phone" testId="add-e1-phone" field="emergency_contact_phone" value={f.emergency_contact_phone} onChange={(v) => up("emergency_contact_phone", v)} format="phone" />
          <Field label="City" testId="add-e1-city" value={f.emergency_contact_city} onChange={(v) => up("emergency_contact_city", v)} />
        </Row>
        <Field
          label="Other number or email (optional)"
          testId="add-e1-other"
          value={f.emergency_contact_other}
          onChange={(v) => up("emergency_contact_other", v)}
          placeholder="Second phone, work #, or backup email"
        />
        <Check
          testId="e1-may-release"
          checked={f.emergency_contact_may_release}
          onChange={(v) => up("emergency_contact_may_release", v)}
          label={<span>My child may be released to this emergency contact</span>}
        />

        <div className="text-sm uppercase tracking-[0.14em] font-bold text-stone-600 mt-5">Emergency contact #2 (recommended)</div>
        <Row>
          <Field label="Name" testId="add-e2-name" value={f.emergency2_contact_name} onChange={(v) => up("emergency2_contact_name", v)} />
          <Field label="Relationship" testId="add-e2-rel" value={f.emergency2_contact_relation} onChange={(v) => up("emergency2_contact_relation", v)} />
        </Row>
        <Field label="Phone" testId="add-e2-phone" value={f.emergency2_contact_phone} onChange={(v) => up("emergency2_contact_phone", v)} format="phone" />
        <Field
          label="Other number or email (optional)"
          testId="add-e2-other"
          value={f.emergency2_contact_other}
          onChange={(v) => up("emergency2_contact_other", v)}
          placeholder="Second phone, work #, or backup email"
        />
        {f.emergency2_contact_name && (
          <Check
            testId="e2-may-release"
            checked={f.emergency2_contact_may_release}
            onChange={(v) => up("emergency2_contact_may_release", v)}
            label={<span>My child may be released to this emergency contact</span>}
          />
        )}
      </Section>
    </div>
  );
}

/* ---------- Page 2 ---------- */
function Page2({ f, up }) {
  return (
    <div className="space-y-5">
      <p className="text-sm text-stone-600 italic">All questions default to <b>No</b>. Switching to <b>Yes</b> on any medical item that requires a care plan will reveal the details field.</p>

      <Section title="Allergies">
        <YesNo testId="allergies-present" label="Does your child have any food, medication, or environmental allergies?"
          value={f.allergies_present} onChange={(v) => up("allergies_present", v)} />
        {f.allergies_present === "Yes" && (
          <>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-stone-500 mt-2">Check all that apply</div>
            <Check testId="allergy-food" label="Food" checked={f.allergy_food} onChange={(v) => up("allergy_food", v)} />
            <Check testId="allergy-medication" label="Medication" checked={f.allergy_medication} onChange={(v) => up("allergy_medication", v)} />
            <Check testId="allergy-environmental" label="Environmental" checked={f.allergy_environmental} onChange={(v) => up("allergy_environmental", v)} />
            <Textarea data-testid="allergies-detail" placeholder="List and explain — foods, reactions, severity" rows={3}
              value={f.allergies_detail} onChange={(e) => up("allergies_detail", e.target.value)} className="mt-2" />
          </>
        )}
        <YesNo testId="allergy-monitoring" label="Does the allergy require staff to monitor or give emergency medication?"
          value={f.allergy_monitoring} onChange={(v) => up("allergy_monitoring", v)}
          help="If Yes, a DCY 01236 Care Plan is required." />
      </Section>

      <Section title="Developmental / special health or medical conditions">
        <YesNo testId="health-condition" label="Does your child have a developmental delay or special health / medical condition?"
          value={f.health_condition_present} onChange={(v) => up("health_condition_present", v)} />
        {f.health_condition_present === "Yes" && (
          <Textarea data-testid="health-condition-detail" placeholder="Please explain" rows={3}
            value={f.health_condition_detail} onChange={(e) => up("health_condition_detail", e.target.value)} />
        )}
        <YesNo testId="health-care-plan" label="Does the condition require staff to perform procedures or give medication during care hours?"
          value={f.health_condition_care_plan} onChange={(v) => up("health_condition_care_plan", v)}
          help="If Yes, a DCY 01236 Care Plan is required." />
      </Section>

      <Section title="Medications & medical foods">
        <YesNo testId="medication-use" label="Is your child currently using any medication or medical food?"
          value={f.medication_use} onChange={(v) => up("medication_use", v)} />
        {f.medication_use === "Yes" && (
          <Textarea data-testid="medication-detail" placeholder="Name, dose, frequency, route" rows={3}
            value={f.medication_detail} onChange={(e) => up("medication_detail", e.target.value)} />
        )}
        <YesNo testId="medication-at-program" label="Does the medication need to be administered at the child care program/home?"
          value={f.medication_at_program} onChange={(v) => up("medication_at_program", v)}
          help="If Yes, a DCY 01217 Request for Administration of Medication is required." />
      </Section>

      <Section title="Dietary restrictions">
        <YesNo testId="dietary-restrictions" label="Any dietary restrictions (medical, religious, cultural)?"
          value={f.dietary_restrictions} onChange={(v) => up("dietary_restrictions", v)} />
        {f.dietary_restrictions === "Yes" && (
          <Textarea data-testid="dietary-detail" placeholder="Please explain" rows={3}
            value={f.dietary_detail} onChange={(e) => up("dietary_detail", e.target.value)} />
        )}
        <div className="mt-2">
          <Label className="text-base font-medium text-stone-800">Modified diet (eliminates milk or a food group)?</Label>
          <Select value={f.dietary_modified_diet} onValueChange={(v) => up("dietary_modified_diet", v)}>
            <SelectTrigger data-testid="dietary-modified" className="mt-1 h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="N/A">N/A — program does not provide meals or snacks</SelectItem>
              <SelectItem value="No">No</SelectItem>
              <SelectItem value="Yes">Yes — HCP written instructions on file</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Section>
    </div>
  );
}

/* ---------- Page 3 ---------- */
function Page3({ f, up }) {
  const rows = [
    { key: "hospitalization", na: "hospitalization_na", val: "hospitalization_history", label: "Hospitalization, outpatient surgery, or previous health concerns", placeholder: "List any that would help staff in an emergency." },
    { key: "comfort", na: "comfort_fears_na", val: "comfort_fears", label: "Comforts / fears", placeholder: "Fears, routines that calm your child." },
    { key: "habits", na: "habits_na", val: "habits", label: "Eating / sleeping habits", placeholder: "Picky eater, nap length, favorite lovey, etc." },
    { key: "routines", na: "routines_behavior_na", val: "routines_behavior", label: "Routines / behavior needs", placeholder: "Special routines, behavior plans." },
  ];
  return (
    <div className="space-y-5">
      <p className="text-sm text-stone-600 italic">Each section below defaults to <b>Not applicable</b>. Uncheck to add details.</p>
      {rows.map((r) => (
        <Section key={r.key} title={r.label}>
          <Check testId={`${r.key}-na`} label="Not applicable" checked={f[r.na]} onChange={(v) => up(r.na, v)} />
          {!f[r.na] && (
            <Textarea data-testid={`${r.key}-detail`} rows={3} placeholder={r.placeholder}
              value={f[r.val]} onChange={(e) => up(r.val, e.target.value)} className="mt-2" />
          )}
        </Section>
      ))}

      {/* iter-80 — new DCY 01234 (Rev 2025+) narrative + acknowledgement
          blocks. All optional. When the parent leaves the two textareas
          blank the printed form auto-ticks the matching NA box, so these
          are safe to skip on a fast enrollment. */}
      <Section title="Information on my child's development (optional)">
        <p className="text-xs text-stone-500 -mt-2">
          Milestones, developmental concerns, or diagnoses the program should be aware of. Prints on Page 2 of DCY 01234.
        </p>
        <Textarea
          data-testid="development-info"
          rows={3}
          placeholder="e.g. Speech delay, meeting milestones ahead of schedule, receiving Help Me Grow services…"
          value={f.development_info}
          onChange={(e) => up("development_info", e.target.value)}
        />
      </Section>

      <Section title="Accommodation that would help meet my child's needs (optional)">
        <p className="text-xs text-stone-500 -mt-2">
          Anything from special seating to a picture-schedule to visual supports. Prints on Page 2 of DCY 01234.
        </p>
        <Textarea
          data-testid="accommodation-notes"
          rows={3}
          placeholder="e.g. Uses a picture schedule at transitions. Needs a quieter nap area."
          value={f.accommodation_notes}
          onChange={(e) => up("accommodation_notes", e.target.value)}
        />
      </Section>

      <Section title="Specialized / individual services">
        <Check
          testId="specialized-services-present"
          checked={f.specialized_services_present}
          onChange={(v) => up("specialized_services_present", v)}
          label={<span>My child <b>will receive</b> specialized or individual services at the program (early intervention, speech, OT/PT, IEP, IFSP, etc.)</span>}
        />
        <p className="text-xs text-stone-500 mt-1">
          Leaving this unchecked auto-ticks &ldquo;N/A&rdquo; on the printed form.
        </p>
      </Section>

      <Section title="Health Care Plan">
        <Check
          testId="health-care-plan-on-file"
          checked={f.health_care_plan_on_file}
          onChange={(v) => up("health_care_plan_on_file", v)}
          label={<span>Our center has a Health Care Plan, physician documentation, or an electronic equivalent for this child on file (DCY 01236 / medical care plan / IHP)</span>}
        />
        <p className="text-xs text-stone-500 mt-1">
          Leaving this unchecked auto-ticks &ldquo;Not Applicable&rdquo; on the printed form.
        </p>
      </Section>
    </div>
  );
}

/* ---------- Page 4 (Services & Sign) — iter-81c1 mirrors DCY 01234 Page 2 ---------- */
function Page4({ f, up, isEdit }) {
  return (
    <div className="space-y-5">
      {/* --- DCY 01234 Page 2 fields moved here from the old Page 3 so the
          wizard now has 2 tabs total, matching the printed form. --- */}
      <Section title="Information on my child's development (optional)">
        <p className="text-xs text-stone-500 -mt-2">
          Milestones, developmental concerns, or diagnoses the program should be aware of. Prints on Page 2 of DCY 01234. Leave blank to auto-tick "N/A".
        </p>
        <Textarea
          data-testid="development-info"
          rows={3}
          placeholder="e.g. Speech delay, meeting milestones ahead of schedule, receiving Help Me Grow services…"
          value={f.development_info}
          onChange={(e) => up("development_info", e.target.value)}
        />
      </Section>

      <Section title="Accommodation that would help meet my child's needs (optional)">
        <p className="text-xs text-stone-500 -mt-2">
          Anything from special seating to a picture-schedule to visual supports. Prints on Page 2 of DCY 01234. Leave blank to auto-tick "N/A".
        </p>
        <Textarea
          data-testid="accommodation-notes"
          rows={3}
          placeholder="e.g. Uses a picture schedule at transitions. Needs a quieter nap area."
          value={f.accommodation_notes}
          onChange={(e) => up("accommodation_notes", e.target.value)}
        />
      </Section>

      <Section title="Specialized / individual services at the program">
        <Check
          testId="specialized-services-present"
          checked={f.specialized_services_present}
          onChange={(v) => up("specialized_services_present", v)}
          label={<span>My child <b>will receive</b> specialized or individual services (early intervention, speech, OT/PT, IEP, IFSP, etc.)</span>}
        />
        {f.specialized_services_present && (
          <Field
            label="Name of service provider(s) and frequency"
            testId="specialized-provider"
            value={f.specialized_service_provider}
            onChange={(v) => up("specialized_service_provider", v)}
            placeholder="e.g. Help Me Grow — weekly on Tuesdays"
          />
        )}
        <p className="text-xs text-stone-500 mt-1">
          Leaving this unchecked auto-ticks "N/A" on the printed form.
        </p>
      </Section>

      <Section title="Chronic health condition / Health Care Plan">
        <p className="text-xs text-stone-500 -mt-2">
          Does your child have a chronic health condition that requires the program to observe or monitor symptoms, administer medication, serve medical foods, perform medical procedures, or avoid specific foods / environmental conditions?
        </p>
        <YesNo
          testId="health-care-plan"
          label="If Yes, we require a Health Care Plan (DCY 01236 / physician documentation)."
          value={f.health_care_plan_on_file === true ? "Yes" : (f.health_care_plan_on_file === false ? "No" : "")}
          onChange={(v) => up("health_care_plan_on_file", v === "Yes" ? true : (v === "No" ? false : null))}
        />
        {f.health_care_plan_on_file === true && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="hcp-upload-prompt">
            <div className="font-semibold">Please attach the physician's Health Care Plan.</div>
            <div className="text-xs mt-1">Upload DCY 01236 or the equivalent physician documentation to the child's Records folder after saving this form. We won't invent placeholder text on the printed enrollment.</div>
          </div>
        )}
      </Section>

      <Section title="Emergency Transportation Authorization">
        <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900">
          <div className="font-semibold">The program has permission to secure emergency transportation.</div>
          <div className="text-xs mt-1">This is auto-marked on every enrollment per Ohio center policy.</div>
        </div>
      </Section>

      <Section title="Parent e-signature">
        <p className="text-sm text-stone-600">Draw your signature below or switch to "Type" mode — either counts as a legal e-signature.</p>
        <SignatureBlockMissingWrap fieldKey="signature">
          <SignaturePad
            value={f.signature}
            onChange={(v) => { up("signature", v); }}
            label="Parent / Guardian signature"
            testid="add-signature-pad"
            required
          />
        </SignatureBlockMissingWrap>
        <Row>
          <Field label="Signing email" type="email" testId="add-sig-email" field="signed_by_email" value={f.signed_by_email} onChange={(v) => up("signed_by_email", v)} placeholder={f.parent_email} />
          <Field label="Date" type="date" testId="add-sig-date" value={f.signature_date} onChange={(v) => up("signature_date", v)} />
        </Row>
      </Section>
    </div>
  );
}

/* ---------- Reusable pieces ---------- */
function Section({ title, children }) {
  return (
    <section className="rounded-2xl border border-stone-200 bg-stone-50/50 p-5 space-y-4">
      <div className="text-sm uppercase tracking-[0.18em] font-bold text-emerald-700">{title}</div>
      {children}
    </section>
  );
}

function Row({ children, cols = 2 }) {
  const col = { 2: "md:grid-cols-2", 3: "md:grid-cols-3", 4: "md:grid-cols-4" }[cols] || "md:grid-cols-2";
  return <div className={`grid grid-cols-1 ${col} gap-4`}>{children}</div>;
}

function Field({ label, value, onChange, testId, type = "text", required, placeholder, format, field }) {
  // format="phone" → live-format as (614) 689-6706 while typing.
  const display = format === "phone" ? formatPhone(value) : (value || "");
  const { missing, clear } = useContext(MissingContext);
  const isMissing = field ? missing.has(field) : false;
  return (
    <div>
      <Label className={`text-sm font-medium ${isMissing ? "text-amber-900" : "text-stone-700"}`}>
        {label}{required && <span className="text-red-600"> *</span>}
        {isMissing && <span className="ml-2 text-[11px] font-semibold text-amber-700">(required — please fill)</span>}
      </Label>
      <Input
        data-testid={testId}
        type={type}
        value={display}
        placeholder={placeholder}
        onChange={(e) => {
          const raw = e.target.value;
          // For phone fields, store the digits-only canonical value internally
          // so reload + Pydantic accept any flavour, but display the pretty
          // version. We keep digits but allow the user to type punctuation.
          if (format === "phone") {
            onChange(unformatPhone(raw));
          } else {
            onChange(raw);
          }
          if (field && isMissing) clear(field);
        }}
        required={required}
        className={`mt-1.5 h-12 text-base transition-colors ${
          isMissing
            ? "bg-amber-50 border-amber-400 focus:border-amber-500 focus:ring-amber-300"
            : ""
        }`}
      />
    </div>
  );
}

function Check({ checked, onChange, label, testId }) {
  return (
    <label className="flex items-start gap-3 text-base text-stone-800 cursor-pointer py-1.5">
      <Checkbox data-testid={testId} checked={!!checked} onCheckedChange={(v) => onChange(!!v)} className="mt-1" />
      <span>{label}</span>
    </label>
  );
}

/** Wraps the signature-pad in a soft-yellow border/glow when the required
 * `signature` field is missing at print time. The SignaturePad component
 * doesn't accept a `field` prop directly, so we render the visual affordance
 * on its container instead. */
function SignatureBlockMissingWrap({ fieldKey, children }) {
  const { missing } = useContext(MissingContext);
  const isMissing = missing.has(fieldKey);
  if (!isMissing) return children;
  return (
    <div
      data-testid={`missing-wrap-${fieldKey}`}
      className="rounded-xl ring-2 ring-amber-400 bg-amber-50/60 p-2 relative"
    >
      <div className="absolute -top-2 left-3 bg-amber-100 border border-amber-300 text-amber-900 text-[11px] font-semibold px-2 py-0.5 rounded">
        Required — please sign
      </div>
      {children}
    </div>
  );
}

/**
 * Yes/No radio pair with the "No" option preselected (per DCY 01234 Rev 8/2025 defaults).
 * Displays as two side-by-side pill buttons — clear, printable, matches how the paper form is marked.
 */
function YesNo({ label, value, onChange, testId, help }) {
  return (
    <div>
      <div className="text-base text-stone-800 font-medium">{label}</div>
      {help && <div className="text-sm text-stone-500 mt-1">{help}</div>}
      <div className="mt-2 inline-flex rounded-xl bg-stone-100 p-1" role="radiogroup" aria-label={label}>
        {["No", "Yes"].map((opt) => (
          <button
            key={opt}
            type="button"
            role="radio"
            aria-checked={value === opt}
            data-testid={`${testId}-${opt.toLowerCase()}`}
            onClick={() => onChange(opt)}
            className={`text-sm font-semibold px-5 py-2.5 rounded-lg transition-all ${
              value === opt
                ? opt === "Yes"
                  ? "bg-emerald-700 text-white shadow-sm"
                  : "bg-white text-stone-900 shadow-sm"
                : "text-stone-500 hover:text-stone-900"
            }`}
          >
            {opt === "No" ? "☒ No" : "☐ Yes"}
          </button>
        ))}
      </div>
    </div>
  );
}
